import React from 'react';
import { FormattedMessage } from 'react-intl';
import RelationName from '@/shared/components/RelationName';
import { mapAssignmentGroupName } from '@/shared/services/ObjectDisplayService';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import FormattedDateShort from '@/shared/components/FormattedDateShort';
import classnames from 'classnames';
import FormattedCurrency from '@/shared/components/FormattedCurrency';
import dateFormatService from '@/shared/services/DateFormatService';
import messages from '../reportingMessages';
import RelationHandlingLevel from '@/shared/components/RelationHandlingLevel';
import relationHandlingLevel from '@/shared/components/RelationHandlingLevel';

const getRelationColumnValue = row => {
  const {
    relationId: id,
    relationDisplayName: name,
    relationType: type,
    relationCrm: crm,
    relationAlias: alias,
    relationIsActive: isActive,
    relationHandlingLevel,
    relationHandlingLevelClarification,
  } = row;

  const relation = {
    id,
    name,
    type,
    crm,
    alias,
    isActive,
  };
  return (
    <span>
      <RelationHandlingLevel
        handlingLevel={relationHandlingLevel}
        clarification={relationHandlingLevelClarification}
      />
      <RelationName relation={relation} />
    </span>
  );
};
const getAssignmentColumnValue = (row, intl) => {
  const { assignmentName, assignmentTemplateName, assignmentTemplateId } = row;
  return mapAssignmentGroupName(assignmentTemplateId, assignmentTemplateName, assignmentName, intl);
};

const HoursColumn = ({ value, className } = {}) => (
  <span className={classnames('text-right hours', { 'text-grey': !value }, className)}>
    {!value && value !== 0 ? null : value.toTime()}
  </span>
);
const AmountColumn = ({ value, className } = {}) => (
  <span className={classnames('text-right amount', { 'text-grey': !value }, className)}>
    {!value && value !== 0 ? null : <FormattedCurrency value={value} />}
  </span>
);
const DateColumn = ({ value }) => (value ? <FormattedDateShort value={value} /> : null);

export const getColumns = (intl, monthColumns, columnStatus, showAssignments, totals) => {
  const columns = [
    {
      Header: <FormattedMessage {...messages.Relation} />,
      Footer: <FormattedMessage {...messages.Totals} />,
      id: 'relation',
      accessor: originalRow => getRelationColumnValue(originalRow),
      isVisible: columnStatus.relation,
      minWidth: 70,
    },

    {
      Header: <FormattedMessage {...messages.Assignment} />,
      id: 'assignment',
      accessor: originalRow => getAssignmentColumnValue(originalRow, intl),
      isVisible: columnStatus.relation && showAssignments,
      minWidth: 70,
    },
    {
      Header: <FormattedMessage {...messages.InvoicingType} />,
      id: 'invoicingType',
      accessor: originalRow => (
        <span className={classnames({ 'text-danger': !originalRow.invoiceStreamIsActive })}>
          {originalRow.invoicingScheduleId
            ? intl.formatMessage(
                listItemMsg(
                  'invoiceschedule',
                  originalRow.invoicingScheduleId,
                  originalRow.invoicingScheduleName,
                ),
              )
            : intl.formatMessage(messages.None)}
        </span>
      ),
      isVisible: columnStatus.invoicingType,
    },
    {
      Header: <FormattedMessage {...messages.LastInvoiceDate} />,
      id: 'lastInvoiceDate',
      accessor: 'lastInvoiceDate',
      Cell: DateColumn,
      isVisible: columnStatus.lastInvoiceDate,
      width: 100,
    },
    {
      Header: <FormattedMessage {...messages.NextScheduleDate} />,
      id: 'nextScheduleDate',
      accessor: 'scheduleDate',
      Cell: DateColumn,
      isVisible: columnStatus.nextScheduleDate,
      width: 100,
    },
    {
      Header: <FormattedMessage {...messages.NextScheduleAmount} />,
      id: 'nextScheduleAmount',
      accessor: 'scheduleAmount',
      Cell: AmountColumn,
      isVisible: columnStatus.nextScheduleAmount,
      align: 'right',
      width: 100,
    },
    {
      Header: <FormattedMessage {...messages.FixedAmountOrAdvance} />,
      id: 'fixedAmount',
      accessor: 'fixedAmount',
      Cell: AmountColumn,
      Footer: () => AmountColumn({ value: totals.totalFixedAmount }),
      isVisible: columnStatus.fixedAmount,
      align: 'right',
      width: 120,
    },
    {
      Header: <FormattedMessage {...messages.DraftInvoiceAmount} />,
      id: 'draftAmount',
      accessor: 'draftAmount',
      Cell: AmountColumn,
      Footer: () => AmountColumn({ value: totals.totalDraftAmount }),
      isVisible: columnStatus.draftInvoiceAmount,
      align: 'right',
      width: 120,
    },
    {
      Header: <FormattedMessage {...messages.Potential} />,
      id: 'potential',
      accessor: 'potential',
      Cell: AmountColumn,
      Footer: () => AmountColumn({ value: totals.totalPotential }),
      isVisible: columnStatus.potential,
      align: 'right',
      width: 120,
    },
    {
      Header: <FormattedMessage {...messages.ToInvoice} />,
      id: 'toInvoice',
      accessor: 'toInvoice',
      Cell: AmountColumn,
      Footer: () => AmountColumn({ value: totals.totalToInvoice }),
      isVisible: columnStatus.toInvoice,
      align: 'right',
      width: 120,
    },

    {
      Header: <FormattedMessage {...messages.AvgAmountPerHour} />,
      id: 'avgAmountPerHour',
      accessor: originalRow =>
        originalRow.toInvoiceHours === 0
          ? null
          : originalRow.toInvoice / originalRow.toInvoiceHours,
      Cell: AmountColumn,
      Footer: () =>
        AmountColumn({
          value:
            totals.totalToInvoiceHours === 0
              ? null
              : totals.totalToInvoice / totals.totalToInvoiceHours,
        }),
      isVisible: columnStatus.avgAmountPerHour,
      align: 'right',
      width: 100,
    },
    {
      Header: <FormattedMessage {...messages.BilledAmount} />,
      id: 'totalAmount',
      accessor: 'totalAmount',
      Cell: AmountColumn,
      Footer: () => AmountColumn({ value: totals.totalAmount }),
      isVisible: columnStatus.billedAmount,
      align: 'right',
      width: 120,
    },
    {
      Header: <FormattedMessage {...messages.WrittenOffAmount} />,
      id: 'writtenOffInternally',
      accessor: 'writtenOffInternally',
      Cell: AmountColumn,
      Footer: () => AmountColumn({ value: totals.totalWrittenOffAmount }),
      isVisible: columnStatus.writtenOffInternally,
      align: 'right',
      width: 100,
    },
    {
      Header: <FormattedMessage {...messages.AvgAmountPerHourBilled} />,
      id: 'avgAmountPerHourBilled',
      accessor: originalRow =>
        originalRow.totalHours === 0 ? null : originalRow.totalAmount / originalRow.totalHours,
      Cell: AmountColumn,
      Footer: () =>
        AmountColumn({
          value: totals.totalHours === 0 ? null : totals.totalAmount / totals.totalHours,
        }),
      isVisible: columnStatus.avgAmountPerHourBilled,
      align: 'right',
      width: 100,
    },
    {
      Header: <FormattedMessage {...messages.TotalHoursAmount} />,
      id: 'totalHoursAmount',
      accessor: originalRow => originalRow.toInvoiceHours + originalRow.totalHours,
      Cell: HoursColumn,
      Footer: () => HoursColumn({ value: totals.totalToInvoiceHours + totals.totalHours }),
      isVisible: columnStatus.totalHoursAmount,
      align: 'right',
      width: 100,
    },
    {
      Header: <FormattedMessage {...messages.ToInvoiceHours} />,
      id: 'toInvoiceHours',
      accessor: 'toInvoiceHours',
      Cell: HoursColumn,
      Footer: () => HoursColumn({ value: totals.totalToInvoiceHours }),
      isVisible: columnStatus.toInvoiceHours,
      align: 'right',
      width: 100,
    },
    {
      Header: <FormattedMessage {...messages.BilledHours} />,
      id: 'totalHours',
      accessor: 'totalHours',
      Cell: HoursColumn,
      Footer: () => HoursColumn({ value: totals.totalHours }),
      isVisible: columnStatus.billedHours,
      align: 'right',
      width: 100,
    },
    ...monthColumns.map((col, idx) => ({
      Header: (
        <span>
          {col.month}
          {dateFormatService.delimiter()}
          {col.year}
        </span>
      ),
      id: `${col.year}-${col.month}`,
      accessor: originalRow => originalRow.amounts && originalRow.amounts[idx],
      Cell: AmountColumn,
      Footer: () => AmountColumn({ value: col.totalAmount }),
      isVisible: columnStatus.months,
      align: 'right',
      width: 100,
    })),
  ];
  // Filter columns on isVisible columns
  return columns
    .map(x => {
      if (typeof x.isVisible !== 'undefined' && !x.isVisible) return null;
      if (typeof x.columns === 'undefined') return x;
      const subColumns = x.columns.filter(c => typeof c.isVisible === 'undefined' || !!c.isVisible);
      if (subColumns.length === 0) return null;
      return {
        ...x,
        columns: subColumns,
      };
    })
    .filter(Boolean);
};

export const defaultColumn = {
  // When using the useFlexLayout:
  minWidth: 30, // minWidth is only used as a limit for resizing
  // width: 1, // width is used for both the flex-basis and flex-grow
  maxWidth: 200, // maxWidth is only used as a limit for resizing
};
